import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import * as API from 'api';
import {
  TableContainer,
  CircularProgress,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
} from '@material-ui/core';
import { ArrowLeft, ArrowRight } from '@material-ui/icons';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import CoManagerDocumentsTableRow from './comanager-document-row';

const CoManagerDocuments = API.CoManagerDocuments();

const ITEMS_PER_PAGE = 25;

const columns = [
  {
    id: 'comanager_document_id',
    label: 'Id',
  },
  {
    id: 'question',
    label: 'Question',
  },
  {
    id: 'stars_count',
    label: 'Votes',
  },
  {
    id: 'stars_avg',
    label: 'Average Rating',
  },
];
const ASC = 'asc';
const DSC = 'desc';

const ServiceDealsTableContainer = props => {
  const { search } = props;
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState(ASC);
  const [orderBy, setOrderBy] = useState('question');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingCoManagerDocumentId, setDeletingCoManagerDocumentId] = useState(null);

  const getCoManagerDocumentsQuery = useQuery(['comanager-documents', page, search, order, orderBy], () => {
    return CoManagerDocuments.getAll({
      page,
      count: ITEMS_PER_PAGE,
      search,
      order_by: orderBy,
      order,
    });
  });
  const coManagerDocuments = getCoManagerDocumentsQuery.data?.results || [];
  const totalPages = Math.ceil(getCoManagerDocumentsQuery.data?.total / ITEMS_PER_PAGE) || 0;

  const deleteCoManagerDocumentMutation = useMutation(id => CoManagerDocuments.destroy(id), {
    onSuccess: () => {
      setDeleteModalOpen(false);
      getCoManagerDocumentsQuery.refetch();
    },
    onError: error => {
      alert(error);
      deleteCoManagerDocumentMutation.reset();
    },
  });

  const handleRequestSort = id => {
    const isAsc = orderBy === id && order === ASC;
    setOrder(isAsc ? DSC : ASC);
    setOrderBy(id);
  };

  const deleteCoManagerDocument = () => deleteCoManagerDocumentMutation.mutateAsync(deletingCoManagerDocumentId);

  const pageSelect = (
    <Box>
      <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
        <ArrowLeft />
      </Button>
      on page #{page + 1} of {totalPages}
      <Button disabled={page === totalPages - 1} onClick={() => setPage(page + 1)}>
        <ArrowRight />
      </Button>
    </Box>
  );

  const deleteModal = (
    <Dialog
      open={deleteModalOpen}
      onClose={() => setDeleteModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        Are you sure to delete question/answer combo {deletingCoManagerDocumentId}?
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">This action cannot be undone.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button disabled={deleteCoManagerDocumentMutation.isLoading} onClick={() => setDeleteModalOpen(false)}>
          No
        </Button>
        <Button disabled={deleteCoManagerDocumentMutation.isLoading} onClick={deleteCoManagerDocument} autoFocus>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const handleDeleteOnClick = id => {
    setDeletingCoManagerDocumentId(id);
    setDeleteModalOpen(true);
  };

  return (
    <TableContainer component={Paper}>
      {getCoManagerDocumentsQuery.isLoading && <CircularProgress />}
      {pageSelect}
      <Table>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.id} sortDirection={orderBy === column.id ? order : false}>
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : ASC}
                  onClick={() => handleRequestSort(column.id)}>
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {coManagerDocuments.map(doc => (
            <CoManagerDocumentsTableRow
              key={doc.comanager_document_id}
              columns={columns}
              data={doc}
              handleDeleteOnClick={handleDeleteOnClick}
            />
          ))}
        </TableBody>
      </Table>
      {pageSelect}
      {deleteModal}
    </TableContainer>
  );
};

export default ServiceDealsTableContainer;
